import { createClient } from '@/lib/supabase/client';

export interface ApplicantData {
  fullName: string
  email: string
  phone: string
  occupation: string
  netWorth: string
  memberReferences: string
}

export interface ApplicationPayment {
  id: string
  amount: number
  payment_status: 'pending' | 'completed' | 'failed' | 'refunded'
  payment_date: string | null
  transaction_id: string | null
  created_at: string
  updated_at: string
  application_id: string
  membership_applications?: {
    id: string
    full_name: string
    email: string
    phone: string
    status: 'pending' | 'under_review' | 'approved' | 'rejected'
    submission_date: string
  }
}

export const paymentService = {
  /**
   * Get all application payments with related application data
   */
  async getAll(): Promise<ApplicationPayment[]> {
    const supabase = createClient()

    const { data, error } = await supabase
      .from('application_payments')
      .select(`
        *,
        membership_applications (
          id,
          full_name,
          email,
          phone,
          status,
          submission_date
        )
      `)
      .order('created_at', { ascending: false })

    if (error) {
      throw new Error(error.message || 'Failed to fetch payments')
    }

    return data || []
  },

  /**
   * Get payment by application ID
   */
  async getByApplicationId(applicationId: string): Promise<ApplicationPayment | null> {
    const supabase = createClient()

    const { data, error } = await supabase
      .from('application_payments')
      .select(`
        *,
        membership_applications (
          id,
          full_name,
          email,
          phone,
          status,
          submission_date
        )
      `)
      .eq('application_id', applicationId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        return null
      }
      throw new Error(error.message || 'Failed to fetch payment')
    }

    return data
  },

  /**
   * Create a payment intent for application fee via Supabase Edge Function
   */
  async createApplicationPaymentIntent(applicantData: ApplicantData, applicationId: string) {
    const supabase = createClient()

    const { data, error } = await supabase.functions.invoke('create-application-payment-intent', {
      body: {
        applicantData,
        applicationId
      }
    })

    if (error) {
      throw new Error(error.message || 'Failed to create payment intent')
    }

    return data
  },

  /**
   * Confirm payment via Supabase Edge Function
   */
  async confirmApplicationPayment(paymentIntentId: string) {
    const supabase = createClient()

    const { data, error } = await supabase.functions.invoke('confirm-application-payment', {
      body: { paymentIntentId }
    })

    if (error) {
      throw new Error(error.message || 'Failed to confirm payment')
    }

    return data
  },

  /**
   * Format amount for display
   */
  formatAmount(amount: number, currency: string = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100)
  }
}